import { ParentUpdateResults } from "./-components/parent-update-list";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useDebounce from "@/hooks/useDebounce";
import { useParentClassUpdatesGetClasses, useParentClassUpdatesGetFeed } from "@/orval/parent-class-updates";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCwIcon, SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/feed")({
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const [classId, setClassId] = useState<string>("all");
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 300);
  const { data: classesData } = useParentClassUpdatesGetClasses(organizationId);
  const params = {
    pageIndex: 0,
    pageSize: 25,
    classId: classId === "all" ? undefined : Number(classId),
    q: debouncedQ.trim() || undefined,
  };
  const { data, isLoading, refetch, isFetching } = useParentClassUpdatesGetFeed(organizationId, params);

  const classOptions = useMemo(
    () =>
      classesData?.semesters.flatMap((semester) =>
        semester.studentGroups.flatMap((group) => group.classes.map((classItem) => classItem)),
      ) ?? [],
    [classesData],
  );
  const uniqueClasses = useMemo(
    () => Array.from(new Map(classOptions.map((classItem) => [classItem.id, classItem])).values()),
    [classOptions],
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
      <PortalPageHeader className="mb-4" title="Updates" subtitle="Class updates for enrolled students" />

      <div className="mb-4 grid gap-2 md:grid-cols-[18rem_1fr_auto]">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger>
            <SelectValue placeholder="All classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classes</SelectItem>
            {uniqueClasses.map((classItem) => (
              <SelectItem key={classItem.id} value={classItem.id.toString()}>
                {classItem.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          startAdornment={<SearchIcon className="size-4" />}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Search updates"
        />
        <Button variant="outline" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCwIcon className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <ParentUpdateResults
        organizationId={organizationId}
        isLoading={isLoading}
        updates={data?.items}
        emptyMessage={getEmptyFeedMessage(q, classId)}
      />
    </div>
  );
}

function getEmptyFeedMessage(query: string, classId: string) {
  if (query && classId !== "all") {
    return "No updates in this class match your search.";
  }
  if (query) {
    return "No updates match your search.";
  }
  return classId !== "all" ? "No updates for this class yet." : "No updates yet.";
}
